import { useOpenModal } from "@customer-app/state/actionCreators/modal";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useMemo } from "react";
import { LoyaltyStoreAccess } from "../../store";
import { Stack, Typography } from "@mui/material";
import { Question } from "akar-icons";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";
import { DisplayPoints } from "./components/DisplayPoints";
import capitalize from 'lodash/capitalize';
import { ProgressBar } from "../../../Subscription/Modals/components/Bundles/ProgressBar";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { useShowActiveSubscriberMultiplier } from "../../utils/useShowActiveSubscriberMultiplier";

export const TiersProgressBar = () => {
  const openModal = useOpenModal();
  const { tieredPoints: points } = LoyaltyStoreAccess.usePoints();
  const { pluralUnit } = LoyaltyStoreAccess.useRewardUnits();
  const custRel = useSmartrrCustomerSelector(state => state.auth.customerRelationship);
  const { previousTier, currentTier, nextTier, allTiers } = LoyaltyStoreAccess.useTiers();

  const activeSubscriberMultipler = LoyaltyStoreAccess.useActiveSubscriberMultiplier();
  // unit name is always in plural
  const unitName = LoyaltyStoreAccess.useSuffix(2);
  const showMultiplier = useShowActiveSubscriberMultiplier();

  const pointsTillNextTier = useMemo(() => {
    // If user is at the max tier, nextTier will be undefined and pointsTillNextTier will be 0
    return nextTier && currentTier ? nextTier.minimumPoints - points : 0;
  }, [nextTier, currentTier]);

  const nextYear = useMemo(() => new Date().getFullYear() + 1, []);

  const tierProgress = useMemo(() => {
    if (nextTier && pointsTillNextTier > 0) {
      return points;
    } else if (pointsTillNextTier < 0 && nextTier) {
      // Edge case - When a user has more points than the minimumPoints for the nextTier but they're stuck on the previous tier
      return nextTier.minimumPoints;
    }
    return 0;
  }, [pointsTillNextTier, nextTier]);

  const openGetPointsModal = useCallback(
    () =>
      openModal("HOW_TO_EARN_POINTS", {
        title: typedLocalize("loyalty.getPoints", {
          unitName: pluralUnit,
        }),
      }),
    []
  );

  if (!currentTier || !allTiers) {
    return <React.Fragment />;
  }

  return (
    <AquariusCard data-testid="tiers-progress-bar-wrapper">
      <Stack direction="column" gap="20px">
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="h4"
            style={{ marginBottom: "0px" }}
            className={prefixClassName("loyalty-tiers-status-header")}
          >
            {typedLocalize("loyalty.tiers.tierStatusTitle", {
              name: capitalize(custRel?.firstName),
              tierName: previousTier?.tierName,
            })}
          </Typography>
          <Typography
            variant="caption1"
            style={{ opacity: "70%" }}
            className={prefixClassName("loyalty-tiers-next-year-status")}
          >
            {typedLocalize("loyalty.tiers.nextYearStatus", {
              year: nextYear,
              tierName: currentTier?.tierName,
            })}
          </Typography>
        </Stack>
        <Stack direction="row" gap="80px">
          <DisplayPoints
            className={prefixClassName("loyalty-tiers-points-earned")}
            points={points}
            title={typedLocalize("loyalty.pointsEarned", {
              unitName: capitalize(pluralUnit),
            })}
          />
          {nextTier && pointsTillNextTier > 0 ? (
            <DisplayPoints
              className={prefixClassName("loyalty-tiers-points-till-tier")}
              points={pointsTillNextTier}
              title={typedLocalize("loyalty.tiers.pointsTillTier", {
                unitName: capitalize(pluralUnit),
                tierName: nextTier.tierName,
                year: nextYear,
              })}
            />
          ) : (
            <React.Fragment />
          )}
        </Stack>
        {currentTier ? (
          <ProgressBar
            steps={nextTier ? nextTier.minimumPoints : 0}
            progress={tierProgress}
            config={{
              mainColor: currentTier.tierColor,
              completionColor: currentTier.tierColor,
            }}
          />
        ) : (
          <React.Fragment />
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          data-testid="tiers-progress-point-info-row"
          className={prefixClassName("loyalty-tiers-progress-point-info-row")}
        >
          <Typography
            variant="caption1"
            style={{ opacity: "70%" }}
            className={prefixClassName("loyalty-tiers-disclaimer-copy")}
          >
            {nextTier
              ? typedLocalize("loyalty.tiers.earnPointsToNextTier", {
                  unitName: pluralUnit,
                })
              : typedLocalize("loyalty.tiers.maxTier")}
          </Typography>

          <Stack
            direction="row"
            spacing="7px"
            sx={{ cursor: "pointer", marginTop: "auto" }}
            onClick={openGetPointsModal}
            className={prefixClassName("loyalty-how-to-earn-points")}
            data-testid="loyalty-how-to-earn-points"
          >
            <Typography variant="caption1" style={{ opacity: "70%" }}>
              {showMultiplier
                ? typedLocalize("loyalty.activeSubscriberMultiplier.earning", {
                    unitName,
                    multiplier: activeSubscriberMultipler,
                  })
                : typedLocalize("loyalty.getPoints", {
                    unitName: pluralUnit,
                  })}
            </Typography>
            <Question strokeWidth={2} size={16} style={{ opacity: "70%" }} />
          </Stack>
        </Stack>
      </Stack>
    </AquariusCard>
  );
};
